<template>
    <div class="simple-header">
        <div class="container">
            <div class="row my-4 my-md-0">
                <div class="col-sm-12 col-md-9 offset-md-3 d-flex flex-column flex-md-row align-items-md-end">
                    <div class="w-25">
                        <h1>{{ $t('site.title.media') }}</h1>
                        <h4 v-if="amount !== null" class="gibson-book-italic mb-0">{{ $t('site.subtitle.media_files', {amount: amount}) }}</h4>
                        <div v-else class="placeholder-glow d-flex flex-row">
                            <span class="placeholder w-25"></span>
                            <h4 class="gibson-book-italic mb-0 ms-2 w-75">Media files</h4>
                        </div>
                    </div>
                    <div class="ms-md-auto d-flex flex-column flex-md-row align-items-start align-items-md-end">
                        <div class="select-group">
                            <label style="margin-right: 0 !important;" for="sort-by">{{ $t('site.filters.sort_by') }}</label>
                            <select class="form-select" id="sort-by" v-model="sort">
                                <option v-for="(option, value) of sortOptions" :key="value" :value="value" v-html="option"></option>
                            </select>
                        </div>

                        <button class="btn btn-outline-primary mt-3 mt-md-0 ms-md-3 d-flex align-items-center" @click="layout = layout === 'grid' ? 'list' : 'grid'">
                            {{ $t('site.actions.layout') }}
                            <Grid :style="{opacity: layout === 'list' ? '50%' : 100}"></Grid>
                            <List :style="{opacity: layout === 'grid' ? '50%' : 100}"></List>
                        </button>
                        <template v-if="!selecting && loggedIn">
                            <button v-if="canDownload" class="btn btn-outline-primary mt-3 mt-md-0 ms-md-3 d-flex align-items-center" @click="selecting = !selecting; downloading = true">
                                {{ $t('site.actions.download') }}
                                <Download class="ms-3"></Download>
                            </button>
                            <button class="btn btn-outline-primary mt-3 mt-md-0 ms-md-3 d-flex align-items-center" @click="selecting = !selecting; addingToFavorites = true">
                                {{ $t('site.actions.favorite') }}
                                <Heart class="ms-3"></Heart>
                            </button>
                        </template>
                        <template v-if="selecting && loggedIn">
                            <button class="btn btn-outline-primary mt-3 mt-md-0 ms-md-3 d-flex align-items-center" @click="selecting = !selecting; downloading = false; addingToFavorites = false; selected = []; selectAllPage = false;" :disabled="loading">
                                {{ $t('site.actions.cancel') }}
                                <Close class="ms-3"></Close>
                            </button>
                            <button v-if="selected.length > 0 && downloading" class="btn btn-outline-primary mt-3 mt-md-0 ms-md-3 d-flex align-items-center" @click="downloadSelected()" :disabled="loading">
                                {{ $t('site.actions.download') }}
                                <Download class="ms-3 me-2"></Download>
                                {{ selected.length }}
                            </button>
                            <button v-if="selected.length > 0 && addingToFavorites" class="btn btn-outline-primary mt-3 mt-md-0 ms-md-3 d-flex align-items-center" @click="addSelectedToFavorite()" :disabled="loading">
                                {{ $t('site.actions.add_to_favorites') }}
                                <FullHeart class="ms-3 me-2"></FullHeart>
                                {{ selected.length }}
                            </button>
                        </template>
                    </div>
                </div>
                <div class="d-flex mt-2">
                    <div v-if="selecting && loggedIn && downloading" class="ms-auto justify-end" :class="selectAllPage ? 'select-all-tooltip' : ''">
                        <button :disabled="selectAllPage" @click="selectAllOnPage()" class="btn btn-primary">
                            {{ $t('site.actions.select_all_on_page') }}
                        </button>
                        <span v-if="selectAllPage" :class="selectAllPage ? 'select-all-tooltip-text' : ''">{{ $t('site.alerts.select_all_on_page_disabled') }}</span>
                    </div>
                </div>
            </div>
        </div>

        <MediaDownloadDialog :downloads="downloads"></MediaDownloadDialog>
    </div>
</template>

<script setup lang="ts">
import {ref, defineProps, watch, inject} from "vue";
import { useI18n } from "vue-i18n";
import Download from "@/site/components/Icons/Download.vue";
import Grid from "@/site/components/Icons/Grid.vue";
import List from "@/site/components/Icons/List.vue";
import Heart from "@/site/components/Icons/Heart.vue";
import Close from "@/site/components/Icons/Close.vue";
import Swal from "sweetalert2";
import FullHeart from "@/site/components/Icons/FullHeart.vue";
import {uniq} from "lodash";
import Media from "@/pcm/store/models/Media";
import MediaDownloadDialog from "@/site/components/MediaDownloadDialog.vue";

const { t } = useI18n();

const { selected, toggleMedia } = inject('selected');
const loggedIn = window.global.loggedIn;
const canDownload = window.global.canDownload;

const downloads = ref<number[]>([]);

const sortOptions = {
    'duration': t('site.filters.duration_asc'),
    '-duration': t('site.filters.duration_desc'),
    'id': t('site.filters.created_at_asc'),
    '-id': t('site.filters.created_at_desc'),
}

const selecting = ref<boolean>(false);
const downloading = ref<boolean>(false);
const addingToFavorites = ref<boolean>(false);
const loading = ref<boolean>(false);
const selectAllPage = ref<boolean>(false);

const props = defineProps<{
    sort: string;
    layout: 'grid' | 'list';
    amount: number,
}>();

const emit = defineEmits<{
    (e: 'sort', sort: string): void;
    (e: 'layout', layout: string): void;
    (e: 'selecting', selecting: boolean): void;
    (e: 'selectAllPage', selectAllPage: boolean): void;
}>();

const sort = ref<string>(props.sort);
const layout = ref<'grid' | 'list'>(props.layout);

const downloadSelected = () => {
    loading.value = true;

    window.axios.post('media/download', {
        files: selected.value,
    })
        .then(response => {
            downloads.value.push(response.data.data.id);

            selecting.value = false;
            downloading.value = false;
            selected.value = [];
        })
        .catch(e => {
            if (e.response.data.message === 'max_downloads_reached') {
                Swal.fire('error', t('site.alerts.exceeded_download_limit', {downloads_today: e.response.data.downloads_today, max_downloads: e.response.data.max_downloads_per_day}), 'error');
            } else {
                Swal.fire('error', t('site.alerts.something_wrong'), 'error');
                selecting.value = false;
                downloading.value = false;
                selected.value = [];
            }
        })
        .finally(() => {
            loading.value = false;
        });
}

const addSelectedToFavorite = () => {
    loading.value = true;

    window.axios.post('media/favorite', {
        files: selected.value,
    })
        .then(() => {
            Swal.fire('success', t('site.alerts.media_added_to_favorites'), 'success')
        })
        .catch(() => {
            Swal.fire(
                'error',
                t('site.alerts.something_wrong'),
                'error'
            );
        })
        .finally(() => {
            loading.value = false;
            selecting.value = false;
            addingToFavorites.value = false;
            selected.value = [];
        });
}

const selectAllOnPage = () => {
    selectAllPage.value = true;
}

watch(() => sort.value, (sort: string) => {
   emit('sort', sort);
});

watch(() => layout.value, (layout: string) => {
    emit('layout', layout);
});

watch(() => selecting.value, (selecting: boolean) => {
    emit('selecting', selecting);
});

watch(() => selectAllPage.value, (selectAllPage: boolean) => {
    emit('selectAllPage', selectAllPage);
});
</script>
